import React, { useState, useEffect } from 'react';
import { capitalizeFirst, errorMsg, homeUrl, rearrangeArray, success } from '../utils/common_utils';
import IndefiniteProgressBar from "../ui_components/indefinite_progress_bar";
import {  getUIState, getUser, isPremiumUser, refreshHomePageDetails } from '../login/auth_utils';
import { LockIcon, SearchIcon, UnlockIcon } from '../ui_components/common_icons';
import { GenericPremiumOneLineComponent, PremiumOneLineComponent } from '../premium/premium_discounts';
import { AIInterviewerLinkHomePage, DSAPageLinkHomePage, PreparationLinkHomePage } from '../preparation_plans/preparation_home_page_link';
import { DoNotLikeCodeZymFormButton } from '../customer_support/do_not_like_codezym';
import { MockInterviewTopmateHomePage } from './mock_interview';
import { LinkUI, LinkUIBasic, LinkUIBlueBold } from '../ui_components/common_links';
import { CodeZymPremiumWithMock } from '../premium/premium_with_mock_interview';
import { BRR, ExpandCloseTab, SPACE, SPACE2 } from '../ui_components/common_ui_components'
import { BulkSubscriptionOneLine } from '../premium/bulk_subscription';
import { CompanyMetadataListSingleRowUI, QuestionDifficultyTagUI, TagsListUI } from './tags_list_ui';
import { addCompanyUrlsInPlace, orderCompaniesByQuery, questionUrl, removeAfterComma } from './question_utils';
import { ConnectAndBook } from '../ui_components/connect_and_book';
import PosthogService from '../tracking_posthog/posthog_tracking';
import { PostHog } from 'posthog-js';
import HomeActionCards from './home_page_top_row_cards';



/**
 *  shows ui on desktop table like
 */
function DesktopQuestionsTable({ questions, query, isPremium }) {
  return (
    <div
      className="
        hidden md:block overflow-x-auto mx-2 md:mx-0
        whitespace-nowrap w-full justify-center items-center
      "
    >
      {/*
      overflow-x-auto is needed to make it scroll separately from parent 
      forcing column to be in single line using whitespace-nowrap
      mx-auto in below table tag is for centering the table
      */}

      <table className="table-auto mx-auto border-collapse md:w-4/5 py-8">
        {/* used overflow-x-auto because I want scroll bars to be visible */}
        <thead>
          <tr
            className="
              text-gray-700 bg-white
              items-center justify-center text-center
            "
          >
            {!isPremium &&
              // shows lock/unlock icons when user is not premium
              <th className="px-2 py-3 text-left pl-6 text-gray-400 font-normal">
                {" "}
              </th>
            }

            <th className="w-8 text-gray-400 font-normal">
              Id
            </th>

            <th className="px-2 py-3 text-left text-gray-400 font-normal">
              Title
            </th>

            <th className="px-2 py-3 text-left text-gray-400 font-normal">
              Companies
            </th>

            <th className="px-2 pl-6 ml-6 py-3 text-left text-gray-400 font-normal">
              Design Pattern
            </th>

            <th className="px-2 py-3 ml-2 mr-4 text-gray-400 font-normal">
              Difficulty
            </th>
          </tr>
        </thead>

        <tbody className="font-normal">
          {questions.map((question) => (
            <tr
              key={question.questionId}
              className="border-b border-gray-200 hover:text-blue-700 even:bg-white odd:bg-gray-100"
            >
              {
                // show lock/unlock icons column only if user is not premium else hide it.
                !isPremium &&
                <td className="pl-6 py-3">
                  {question.type == 1 && !isPremium && <LockIcon />}
                  {(question.type != 1 || isPremium) && <UnlockIcon />}
                </td>
              }

              <td>
                <span
                  className="
                    px-4 text-sm flex items-center justify-center
                    text-center text-gray-500
                  "
                >
                  {question.questionId}.
                </span>
              </td>

              <td className="px-4 pl-1 py-3">
                <a
                  href={questionUrl(question)}
                  className="text-black hover:text-blue-600"
                >
                  {question.heading}
                </a>
              </td>

              <td className="px-4 pl-1 py-3">
                {question.companies &&
                  <TagsListUI
                    title={`${question.questionId}. ${question.heading}`}
                    tags={orderCompaniesByQuery(question.companies, query)}
                  />
                }
              </td>

              <td className="px-2 pl-6 ml-6 py-3 text-gray-500">
                {removeAfterComma(question.designPatterns)}
              </td>

              <td>
                <div className="px-4">
                  <QuestionDifficultyTagUI difficulty={question.difficulty} />
                </div>
              </td>

              {false &&
                <td>
                  <div
                    className={`text-lg md:mr-8 md:ml-4 ${
                      question.difficulty === 1 ? 'text-orange-500' :
                        question.difficulty === 2 ? 'text-red-600' :
                          'text-green-500'
                    }`}
                  >
                    {
                      question.difficulty == 1 ? 'Medium' :
                        question.difficulty == 2 ? 'Hard' :
                          'Easy'
                    }
                  </div>
                </td>
              }
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * shows ui on mobile card like
 */
function MobileQuestionsList({ questions, query, isPremium }) {
  const getDifficultyTheme = (difficulty) => {
    if (difficulty == 1) {
      return {
        border: "border-orange-100",
        numberText: "text-orange-600",
        numberBg: "bg-orange-50",
        difficultyText: "text-orange-700",
        difficultyBg: "bg-orange-50"
      };
    }

    if (difficulty == 2) {
      return {
        border: "border-rose-100",
        numberText: "text-rose-600",
        numberBg: "bg-rose-50",
        difficultyText: "text-rose-700",
        difficultyBg: "bg-rose-50"
      };
    }

    return {
      border: "border-emerald-100",
      numberText: "text-emerald-600",
      numberBg: "bg-emerald-50",
      difficultyText: "text-emerald-700",
      difficultyBg: "bg-emerald-50"
    };
  };

  const getDifficultyText = (difficulty) => {
    if (difficulty == 1) return "Medium";
    if (difficulty == 2) return "Hard";
    return "Easy";
  };

  return (
    <div className="md:hidden w-full px-3 mt-3 space-y-4">
      {questions.map((question) => {
        const theme = getDifficultyTheme(question.difficulty);

        return (
          <div
            key={question.questionId}
            className={`
              rounded-3xl bg-white border ${theme.border}
              shadow-[0_8px_24px_rgba(15,23,42,0.06)]
              overflow-hidden
            `}
          >
            <div className="p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div
                    className={`
                      h-12 w-12 rounded-2xl
                      ${theme.numberBg}
                      flex items-center justify-center
                    `}
                  >
                    <span className={`text-lg font-bold ${theme.numberText}`}>
                      {question.questionId}
                    </span>
                  </div>

                  {!isPremium &&
                    <div className="mt-2 flex justify-center">
                      {question.type == 1 && !isPremium && <LockIcon />}
                      {(question.type != 1 || isPremium) && <UnlockIcon />}
                    </div>
                  }
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <a
                      href={questionUrl(question)}
                      className="text-base font-semibold text-slate-950 leading-snug hover:text-blue-600"
                    >
                      {question.heading}
                    </a>

                    <a
                      href={questionUrl(question)}
                      className="text-slate-300 text-xl leading-none"
                      aria-label={`Open ${question.heading}`}
                    >
                      ›
                    </a>
                  </div>

                  {question.companies &&
                    <div className="mt-2 overflow-hidden">
                      <TagsListUI
                        title={`${question.questionId}. ${question.heading}`}
                        tags={orderCompaniesByQuery(question.companies, query)}
                      />
                    </div>
                  }
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  {question.designPatterns &&
                    <div className="text-sm font-medium text-slate-700 truncate">
                      {removeAfterComma(question.designPatterns)} Pattern
                    </div>
                  }
                </div>

                <div className="flex justify-end">
                  <span
                    className={`
                      rounded-full px-4 py-1 text-sm font-semibold
                      ${theme.difficultyBg} ${theme.difficultyText}
                    `}
                  >
                    {getDifficultyText(question.difficulty)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export {MobileQuestionsList, DesktopQuestionsTable}